import type { ApiError } from '@/api/types';

export const __prod__ = process.env.NODE_ENV === 'production';
export const publicUrl = process.env.PUBLIC_URL;
export const apiUrl = process.env.NEXT_PUBLIC_API_URI;
export const isStaging = process.env.NEXT_PUBLIC_IS_STAGING === 'true';
export const baseUrl = process.env.NEXT_PUBLIC_APP_URI || 'https://ahamove.com';
export const domainUrl = process.env.NEXT_PUBLIC_APP_DOMAIN || 'ahamove.com';
export const baseUrlEP = process.env.NEXT_PUBLIC_APP_API_EP;
export const APIKEY = process.env.NEXT_PUBLIC_APP_APIKEY || '';

export const PAGE_INDEX = 0;
export const PAGE_SIZE = 22;

export const API_FETCH_TIMEOUT = 30;

export enum ERROR_CODE {
  NOT_ENOUGH_CREDIT = 'NOT_ENOUGH_CREDIT',
  NOT_AUTHORIZED = 'NOT_AUTHORIZED',
  REFRESH_TOKEN_HAS_EXPIRED = 'REFRESH_TOKEN_HAS_EXPIRED',
  INVALID_DATA = 'INVALID_DATA',
  AMS_ALREADY_HAVE_ANOTHER_RENTING_ORDER = 'AMS_ALREADY_HAVE_ANOTHER_RENTING_ORDER',
  BAD_REQUEST = 'BAD_REQUEST',
  NOT_ENOUGH_BALANCE = 'NOT_ENOUGH_BALANCE',
  AMS_INVALID_KYC_PROFILE = 'AMS_INVALID_KYC_PROFILE',
}

export const ERROR_CODES: Record<ERROR_CODE, ApiError> = {
  NOT_ENOUGH_CREDIT: {
    code: 'NOT_ENOUGH_CREDIT',
    description: 'Tài khoản không đủ tiền hoặc không phải là tài khoản công nợ',
    http_code: 406,
    internal: '',
    title: 'Not enough credit',
  },
  NOT_AUTHORIZED: {
    code: 'NOT_AUTHORIZED',
    description:
      'Không có quyền thực hiện thao tác này, vui lòng kiểm tra lại.',
    http_code: 401,
    title: 'Thao tác bị từ chối.',
  },
  REFRESH_TOKEN_HAS_EXPIRED: {
    code: 'REFRESH_TOKEN_HAS_EXPIRED',
    http_code: 401,
    title: 'Token expired',
    description: 'Your token has expired, please sign in again.',
    internal: '',
  },
  INVALID_DATA: {
    code: 'INVALID_DATA',
    http_code: 400,
    title: 'Invalid data',
    description: 'Dữ liệu gửi lên chưa đúng, vui lòng kiểm tra lại',
    internal: '',
  },
  AMS_ALREADY_HAVE_ANOTHER_RENTING_ORDER: {
    code: 'AMS_ALREADY_HAVE_ANOTHER_RENTING_ORDER',
    http_code: 400,
    title: 'Không thể tạo đơn thuê xe mới',
    description: 'Bạn đã có một đơn thuê xe khác',
    internal: '',
  },
  BAD_REQUEST: {
    code: 'BAD_REQUEST',
    http_code: 400,
    title: 'Thông tin không hợp lệ.',
    description: 'Thông tin không hợp lệ, vui lòng kiểm tra lại.',
    internal: '',
  },
  NOT_ENOUGH_BALANCE: {
    code: 'NOT_ENOUGH_BALANCE',
    http_code: 406,
    title: 'Số dư tài khoản hiện tại của bạn không đủ',
    description: 'Số dư tài khoản hiện tại của bạn không đủ',
    internal: '',
  },
  AMS_INVALID_KYC_PROFILE: {
    code: 'AMS_INVALID_KYC_PROFILE',
    http_code: 404,
    title: 'Hồ sơ không hợp lệ',
    description:
      'Hồ sơ không tồn tại hoặc không hợp lệ! Xin hãy liên hệ với bộ phận chăm sóc khách hàng',
    internal: '',
  },
};

export enum WebInAppEvent {
  CLOSE = 'close',
  BACK = 'back',
  HIDE_TOOLBAR = 'hide_toolbar',
  GET_TOKEN = 'getToken',
  OPEN_PAGE = 'openPage',
  PAYMENT = 'payment',
}

export enum WebInAppID {
  AHA_USER = 'ahauser',
}

export const inAppScreens = {
  balance: `ahamove://balance?open_topup=true`,
  records: `ahamove://records`,
  orderDetail: (orderId: string) =>
    `ahamove://order_detail?pay_now=true&order_id=${orderId}`,
};

export enum UPCOMING_BOOKINGS_STATUS {
  WAITING_TO_PICKUP = 'WAITING_TO_PICKUP',
  PAYING = 'PAYING',
  RENTING = 'RENTING',
  WAITING_TO_RETURN = 'WAITING_TO_RETURN',
  VERIFIED = 'VERIFIED',
  WAITING_TO_VERIFY = 'WAITING_TO_VERIFY',
}

export enum PREVIOUS_BOOKINGS_STATUS {
  CANCELLED = 'CANCELLED',
  CLOSED = 'CLOSED',
}

export enum BookingStatus {
  WAITING_TO_PICKUP = 'WAITING_TO_PICKUP',
  PAYING = 'PAYING',
  RENTING = 'RENTING',
  WAITING_TO_RETURN = 'WAITING_TO_RETURN',
  VERIFIED = 'VERIFIED',
  WAITING_TO_VERIFY = 'WAITING_TO_VERIFY',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  CLOSED = 'CLOSED',
}

export enum RefundStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
}

export const RECORDS_LIST_STATUS = {
  ...UPCOMING_BOOKINGS_STATUS,
  ...PREVIOUS_BOOKINGS_STATUS,
};

export const CANCELABLE_BOOKING_STATUS = [
  BookingStatus.WAITING_TO_PICKUP,
  BookingStatus.PAYING,
];

export type RECORDS_LIST_STATUS = typeof RECORDS_LIST_STATUS;

export const GET_CURRENT_LOCATION_MAX_AGE = 60 * 60 * 1000;
export const GET_CURRENT_LOCATION_TIMEOUT = 5000;
export const DEFAULT_DATE_TIME_FORMAT = 'HH:mm, DD/MM/YYYY';
export const PROMO_DETAIL_DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';
export const DEFAULT_MAXIMUM_START_TIME_FROM_NOW_IN_DAY = 45;
export const DEFAULT_MINIMUM_START_TIME_FORM_NOW_IN_SECOND = 4 * 60 * 60; // 4h
export const DEFAULT_MINIMUM_RENT_TIME_IN_SECOND = 3 * 60 * 60; // 3h
export const SECOND_PER_DAY = 86400;
export const REQUIRED_AUTH_ROUTES = [
  '/checkout',
  '/bookings',
  '/bookings/[id]',
];

export const DEFAULT_TIME_RANGE = [6, 21];
export const DEFAULT_TIME_RANGE_OBJ = {
  BIKE: [6, 21],
  CAR: [7, 21],
};

export const DEFAULT_START_TIME = {
  hour: 8,
  minute: 30,
};

export const cities: Record<string, string> = {
  DAD: 'Đà Nẵng',
  SGN: 'Hồ Chí Minh',
  HAN: 'Hà Nội',
};

export enum RentType {
  BIKE = 'BIKE',
  CAR = 'CAR',
}

export enum EKYC_STATUS {
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
}

export const LOCAL_STORAGE_EV_BOOKING_NOTE = 'ev_booking_note';

export enum EV_TRACK_KEY {
  ev_website_open = 'ev_website_open', // Khi có 1 người khách hàng Truy cập vào website thuexedien.ahamove.com  thì ghi nhận lại. Khách hàng có thể nhập tên domain để truy cập/ nhấn từ 1 quảng cáo/ từ kết quả tìm kiếm trên công cụ tiềm kiếm.
  ev_login = 'ev_login',
  ev_location_choose = 'ev_location_choose', // Chọn địa điểm nhận xe trên web
  ev_location_confirm = 'ev_location_confirm', // Xác nhận địa điểm nhận xe trên web
  ev_time_choose = 'ev_time_choose', // Chọn ngày và giờ trên web
  ev_time_confirm = 'ev_time_confirm', // Xác nhận thời gian thuê xe điện
  ev_find_vehicle_button = 'ev_find_vehicle_button', // Ấn vào nút “tìm xe” trên web.
  ev_price_detail = 'ev_price_detail', // Ấn vào thư mục bảng giá chi tiết, sau khi ấn vào nút tìm xe
  ev_vehicle_type = 'ev_vehicle_type', // Màn hình chọn loại xe, sau khi ấn vào nút "tìm xe"
  ev_vehicle_confirm = 'ev_vehicle_confirm', // Ấn vào nút "tiếp tục" ở bước chọn loại xe thuê
  ev_payment_method = 'ev_payment_method', // Lựa chọn phương thức thanh toán ở mục thanh toán
  ev_payment_cash = 'ev_payment_cash', // Chọn hình thức thanh toán bằng tiền mặt
  ev_payment_balance = 'ev_payment_balance', // Thanh toán bằng số dư trên tài khoản Ahamove
  ev_promotion_code = 'ev_promotion_code', // Ấn vào lựa chọn nhập mã giảm giá
  ev_promotion_use = 'ev_promotion_use', // Sử dụng mã giảm giá thành công. Là khi nhấn sử dụng mã giảm giá và thành công thì có thông báo “Thành công”.
  ev_booking_confirmation_button = 'ev_booking_confirmation_button', // Ấn nút lựa chọn xác nhận thuê xe thành công. Là sau khi nhấn thuê ngay và thành công → chuyển về trang chi tiết đơn thuê.
  ev_booking_cancel = 'ev_booking_cancel', // Ấn nút lựa chọn huỷ đặt xe. Trong chi tiết đơn thuê trạng thái “Paying”/”Waiting_to_pickup”.
  ev_importance_note = 'ev_importance_note', // Ấn vào mục xem các lưu ý quan trọng sau khi đã đặt xe thành công.
  ev_report_issues = 'ev_report_issues', // Ấn vào mục xem cách báo cáo các vấn đề xảy ra trong chi tiết đơn hàng.

  ev_rental_pricing = 'ev_rental_pricing', // Ấn chọn xem bảng giá dịch vụ thuê xe tại Trang chủ
  ev_FAQs = 'ev_FAQs', // Chọn các câu hỏi thường gặp tại Trang chủ
  ev_download_app = 'ev_download_app', // Ấn vào tải app trên Store tại Trang chủ
  ev_ahadelivery_service = 'ev_ahadelivery_service', // Ấn vào mục Dịch vụ Ahadelivery trên trang chủ tại footer trang
  ev_rental = 'ev_rental', // Ấn vào mục cho thuê xe điện trên trang chủ tại footer trang
  ev_ahatruck = 'ev_ahatruck', // Ấn vào mục Dịch vụ xe tải trên trang chủ tại footer trang
  ev_business_service = 'ev_business_service', // Ấn vào mục Dịch vụ cho doanh nghiệp trên trang chủ tại footer trang
  ev_about_us = 'ev_about_us', // Ấn vào mục Về chúng tôi trên trang chủ tại footer trang
  ev_news = 'ev_news', // Ấn vào mục Tin tức trên trang chủ tại footer trang
  ev_recruitment = 'ev_recruitment', // Ấn vào mục Tuyển dụng trên trang chủ tại footer trang
  ev_individual_customer = 'ev_individual_customer', // Ấn vào mục Khách hàng cá nhân trên trang chủ tại footer trang
  ev_business_customer = 'ev_business_customer', // Ấn vào mục Khách hàng doanh nghiệp trên trang chủ tại footer trang
  ev_support_center = 'ev_support_center', // Ấn vào mục Trung tâm hỗ trợ trên trang chủ tại footer trang
  ev_intergration_account = 'ev_intergration_account', // Ấn vào mục Tài khoản tích hợp trên trang chủ tại footer trang
  ev_policy_term = 'ev_policy_term', // Ấn vào mục Chính sách và điều khoản trên trang chủ tại footer trang
  ev_new_driver_sign_up = 'ev_new_driver_sign_up', // Ấn vào mục Đăng ký tài xế mới trên trang chủ tại footer trang
  ev_driver_community = 'ev_driver_community', // Ấn vào mục Cộng đồng tài xế trên trang chủ tại footer trang
  ev_driver_handbook = 'ev_driver_handbook', // Ấn vào mục Cẩm nang tài xế trên trang chủ tại footer trang
  ev_driver_support_center = 'ev_driver_support_center', // Ấn vào mục Trung tâm hỗ trợ tài xế trên trang chủ tại footer trang
  ev_developer_platform = 'ev_developer_platform', // Ấn vào mục Nền tảng nhà phát triển trên trang chủ tại footer trang
}

export enum BANNER_TYPE {
  EV_RENTAL = 'ev-rental',
}
